<template>
  <div>
    <v-card class="mb-3">
      <v-card-text>
        <v-row align="center">
          <v-col>
            <div class="text-h6">
              Caja — {{ register && register.office ? register.office.name : '' }} ({{ registerOpenedAt }})
            </div>
            <div class="subtitle-1">
              <v-chip :color="isOpen ? 'warning' : 'success'" text-color="white" small>
                {{ isOpen ? 'ABIERTA' : 'CERRADA' }}
              </v-chip>
              {{ register && register.office ? register.office.city : '' }}
              Apertura: {{ register ? (register.opening_balance | currency) : '' }} |
              Cajero: {{ register && register.user ? register.user.name : '' }}
            </div>
          </v-col>
          <v-spacer></v-spacer>
          <v-btn v-if="isOpen" color="primary" @click="dialogClose = true">
            <v-icon left>mdi-lock</v-icon> Cerrar Caja
          </v-btn>
          <v-btn text @click="$router.push('/cash-register')">Volver</v-btn>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="mb-3">
      <v-card-title>Ingresos del día</v-card-title>
      <v-data-table
        :headers="incomeHeaders"
        :items="incomes"
        hide-default-footer
        no-data-text="Sin ingresos en el día"
        :items-per-page="100"
        dense
      >
        <template v-slot:[`item.amount`]="{ item }">{{ item.amount | currency }}</template>
        <template v-slot:[`item.payment_method`]="{ item }">
          {{ item.payment_method ? paymentMethodLabel(item.payment_method) : '—' }}
        </template>
        <template v-slot:[`item.source_type`]="{ item }">
          <v-chip small :color="sourceColor(item.source_type)">
            {{ sourceLabel(item.source_type) }}
          </v-chip>
        </template>
        <template v-slot:[`item.tip`]="{ item }">
          <span v-if="getTip(item)">{{ getTip(item).amount | currency }} ({{ getTip(item).employee ? getTip(item).employee.full_name : '' }})</span>
          <span v-else>—</span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom v-if="isOpen">
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="success" v-bind="attrs" v-on="on" small @click="openAddTip(item)">
                mdi-cash-plus
              </v-icon>
            </template>
            <span>Agregar propina</span>
          </v-tooltip>
        </template>
      </v-data-table>
      <v-divider></v-divider>
      <v-card-text class="text-right font-weight-bold text-h6">
        Total ingresos: {{ totalIncome | currency }}
      </v-card-text>
    </v-card>

    <v-card class="mb-3">
      <v-card-title>
        Propinas
        <v-spacer></v-spacer>
        <span class="text-body-2">Total: {{ totalTips | currency }}</span>
      </v-card-title>
      <v-data-table
        :headers="tipHeaders"
        :items="tips"
        hide-default-footer
        no-data-text="Sin propinas registradas"
        :items-per-page="50"
        dense
      >
        <template v-slot:[`item.amount`]="{ item }">{{ item.amount | currency }}</template>
        <template v-slot:[`item.employee`]="{ item }">{{ item.employee ? item.employee.full_name : '—' }}</template>
        <template v-slot:[`item.actions`]="{ item }" v-if="isOpen">
          <v-icon color="error" small @click="deleteTip(item)">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <v-card class="mb-3">
      <v-card-title>
        Gastos
        <v-spacer></v-spacer>
        <span class="text-body-2">Total: {{ totalExpenses | currency }}</span>
        <v-btn v-if="isOpen" icon color="success" small class="ml-2" @click="dialogExpense = true">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-card-title>
      <v-data-table
        :headers="expenseHeaders"
        :items="expenses"
        hide-default-footer
        no-data-text="Sin gastos registrados"
        :items-per-page="50"
        dense
      >
        <template v-slot:[`item.amount`]="{ item }">{{ item.amount | currency }}</template>
        <template v-slot:[`item.actions`]="{ item }" v-if="isOpen">
          <v-icon color="error" small @click="deleteExpense(item)">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <v-card class="mb-3">
      <v-card-title>Resumen {{ isOpen ? 'parcial' : 'del cierre' }}</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <div><b>Apertura:</b> {{ register ? (register.opening_balance | currency) : '' }}</div>
            <div><b>+ Ingresos efectivo:</b> {{ isOpen ? cashIncomeTotal : (register ? (register.cash_income | currency) : '') }}</div>
            <div><b>+ Ingresos electrónicos:</b> {{ isOpen ? electronicIncomeTotal : (register ? (register.electronic_income | currency) : '') }}</div>
            <div><b>- Gastos:</b> {{ totalExpenses | currency }}</div>
            <v-divider class="my-2"></v-divider>
            <div class="text-h6"><b>Total esperado:</b> {{ expectedTotal | currency }}</div>
            <div class="mt-2"><b>Total propinas:</b> {{ totalTips | currency }}</div>
            <template v-if="!isOpen">
              <v-divider class="my-2"></v-divider>
              <div><b>Efectivo contado:</b> {{ register ? (register.actual_cash | currency) : '' }}</div>
              <div :class="cashDiffC !== 0 ? 'error--text' : ''">
                <b>Diferencia en efectivo:</b> {{ register ? ((register.actual_cash - register.cash_income - register.opening_balance + totalExpenses) | currency) : '' }}
              </div>
              <div v-if="register && register.notes" class="mt-2"><b>Notas:</b> {{ register.notes }}</div>
            </template>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialogTip" width="420" persistent>
      <v-card>
        <v-card-title>Agregar propina</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-form ref="formTip" v-model="validTip" lazy-validation>
            <div class="mb-2">
              <b>Ingreso:</b> {{ tipTarget ? tipTarget.reference + ' — ' + tipTarget.client_name : '' }}
            </div>
            <div class="mb-2 text-body-2" v-if="tipTarget && tipTarget.employee_name">
              <b>Asesor de la venta:</b> {{ tipTarget.employee_name }}
            </div>
            <v-autocomplete
              v-model="tipForm.employee_id"
              :items="employees"
              item-text="full_name"
              item-value="id"
              label="Asesor"
              :rules="[r.required]"
              outlined dense
              no-data-text="Sin resultados"
            ></v-autocomplete>
            <v-text-field
              v-model="tipForm.amount"
              :rules="[r.required, r.positive]"
              label="Valor propina"
              type="number"
              outlined dense
              prefix="$"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="error" @click="dialogTip = false">Cancelar</v-btn>
          <v-btn text color="success" :disabled="!validTip" :loading="$store.state.cashRegister.isLoading" @click="doAddTip">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogExpense" width="420" persistent>
      <v-card>
        <v-card-title>Agregar gasto</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-form ref="formExpense" v-model="validExpense" lazy-validation>
            <v-text-field
              v-model="expenseForm.description"
              :rules="[r.required]"
              label="Descripción"
              outlined dense
            ></v-text-field>
            <v-text-field
              v-model="expenseForm.amount"
              :rules="[r.required, r.positive]"
              label="Valor"
              type="number"
              outlined dense
              prefix="$"
            ></v-text-field>
            <v-select
              v-model="expenseForm.category"
              :items="expenseCategories"
              label="Categoría (opcional)"
              outlined dense
              clearable
            ></v-select>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="error" @click="dialogExpense = false">Cancelar</v-btn>
          <v-btn text color="success" :disabled="!validExpense" :loading="$store.state.cashRegister.isLoading" @click="doAddExpense">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogClose" width="550" persistent>
      <v-card>
        <v-card-title>Cerrar Caja</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="6"><b>Apertura:</b> {{ register ? (register.opening_balance | currency) : '' }}</v-col>
            <v-col cols="6"><b>+ Ing. Efectivo:</b> {{ cashIncomeTotal | currency }}</v-col>
            <v-col cols="6"><b>+ Ing. Electrónicos:</b> {{ electronicIncomeTotal | currency }}</v-col>
            <v-col cols="6"><b>- Gastos:</b> {{ totalExpenses | currency }}</v-col>
            <v-col cols="12"><v-divider></v-divider></v-col>
            <v-col cols="6"><b>Esperado en efectivo:</b> {{ expectedCash | currency }}</v-col>
            <v-col cols="6"><b>Total esperado:</b> {{ expectedTotal | currency }}</v-col>
          </v-row>
          <v-divider class="my-3"></v-divider>
          <v-form ref="formClose" v-model="validClose" lazy-validation>
            <v-text-field
              v-model="closeForm.actual_cash"
              :rules="[r.required, r.positive]"
              label="Efectivo contado en caja"
              type="number"
              outlined dense
              prefix="$"
            ></v-text-field>
            <div class="text-h6" :class="cashDiff !== 0 ? 'error--text' : ''">
              Diferencia en efectivo: {{ cashDiff | currency }}
            </div>
            <v-textarea
              v-model="closeForm.notes"
              label="Notas (opcional)"
              outlined dense
              rows="2"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="error" @click="dialogClose = false">Cancelar</v-btn>
          <v-btn text color="primary" :disabled="!validClose" :loading="$store.state.cashRegister.isLoading" @click="doClose">
            Cerrar Caja
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "ManageCashRegister",
  props: { id: { required: true } },
  filters: {
    currency(v) { return new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP" }).format(v); },
    shortDate(v) { return v ? v.substring(0, 16).replace("T", " ") : ""; },
  },
  data: () => ({
    register: null,
    employees: [],
    dialogTip: false,
    dialogExpense: false,
    dialogClose: false,
    validTip: true,
    validExpense: true,
    validClose: true,
    tipTarget: null,
    tipForm: { employee_id: "", amount: "" },
    expenseForm: { description: "", amount: "", category: "" },
    closeForm: { actual_cash: "", notes: "" },
    expenseCategories: ["Transporte", "Oficina", "Servicios", "Marketing", "Otros"],
    incomeHeaders: [
      { text: "Tipo", value: "source_type", sortable: false },
      { text: "Referencia", value: "reference", sortable: false },
      { text: "Cliente", value: "client_name", sortable: false },
      { text: "Valor", value: "amount" },
      { text: "Método", value: "payment_method", sortable: false },
      { text: "Asesor", value: "employee_name", sortable: false },
      { text: "Propina", value: "tip", sortable: false },
      { text: "Usuario", value: "user_name", sortable: false },
      { text: "Acciones", value: "actions", sortable: false },
    ],
    tipHeaders: [
      { text: "Asesor", value: "employee", sortable: false },
      { text: "Valor", value: "amount" },
      { text: "Acciones", value: "actions", sortable: false },
    ],
    expenseHeaders: [
      { text: "Descripción", value: "description" },
      { text: "Categoría", value: "category" },
      { text: "Valor", value: "amount" },
      { text: "Acciones", value: "actions", sortable: false },
    ],
    r: {
      required: (v) => (v !== "" && v != null) || "Campo requerido",
      positive: (v) => parseFloat(v) > 0 || "Debe ser mayor a 0",
    },
  }),
  computed: {
    registerOpenedAt() {
      if (!this.register || !this.register.opened_at) return "";
      return this.register.opened_at.substring(0, 16).replace("T", " ");
    },
    isOpen() { return this.register && this.register.status === "OPEN"; },
    incomes() { return this.register && this.register.incomes ? this.register.incomes : []; },
    tips() { return this.register && this.register.tips ? this.register.tips : []; },
    expenses() { return this.register && this.register.expenses ? this.register.expenses : []; },
    totalIncome() { return this.incomes.reduce((s, i) => s + parseFloat(i.amount || 0), 0); },
    totalTips() { return this.tips.reduce((s, i) => s + parseFloat(i.amount || 0), 0); },
    totalExpenses() { return this.expenses.reduce((s, i) => s + parseFloat(i.amount || 0), 0); },
    cashIncomeTotal() { return this.incomes.filter(i => i.payment_method === 'CASH').reduce((s, i) => s + parseFloat(i.amount || 0), 0); },
    electronicIncomeTotal() { return this.totalIncome - this.cashIncomeTotal; },
    expectedCash() { return (this.register ? parseFloat(this.register.opening_balance) : 0) + this.cashIncomeTotal - this.totalExpenses; },
    expectedTotal() { return (this.register ? parseFloat(this.register.opening_balance) : 0) + this.totalIncome - this.totalExpenses; },
    cashDiff() { return parseFloat(this.closeForm.actual_cash || 0) - this.expectedCash; },
  },
  created() {
    this.loadData();
    this.loadEmployees();
  },
  methods: {
    loadData() {
      this.$store.dispatch("getCashRegister", this.id).then(() => {
        this.register = this.$store.state.cashRegister.register;
      });
    },
    loadEmployees() {
      const user = this.$store.state.user.user;
      const officeId = user ? user.office_id || (user.office && user.office.id) : null;
      const filter = officeId ? "office=" + officeId : "";
      this.$store.dispatch("getEmployeesAll", filter).then(() => {
        const data = this.$store.state.employee.employees;
        this.employees = data.data || data;
      });
    },
    getTip(income) {
      if (income.tips && income.tips.length) {
        return income.tips[0];
      }
      if (income.payment_id && this.tips.length) {
        return this.tips.find(t => t.income_id === income.payment_id);
      }
      return null;
    },
    openAddTip(item) {
      this.tipTarget = item;
      this.tipForm = {
        employee_id: item.employee_id || "",
        amount: "",
      };
      this.dialogTip = true;
    },
    doAddTip() {
      if (!this.$refs.formTip.validate()) return;
      const form = {
        income_id: this.tipTarget.payment_id || null,
        employee_id: this.tipForm.employee_id,
        amount: this.tipForm.amount,
      };
      this.$store.dispatch("addCashRegisterTip", { id: this.id, form }).then(() => {
        this.dialogTip = false;
        this.loadData();
      });
    },
    deleteTip(item) {
      if (!confirm("¿Eliminar esta propina?")) return;
      this.$store.dispatch("deleteCashRegisterTip", { tipId: item.id }).then(() => {
        this.loadData();
      });
    },
    doAddExpense() {
      if (!this.$refs.formExpense.validate()) return;
      const form = {
        description: this.expenseForm.description,
        amount: this.expenseForm.amount,
        category: this.expenseForm.category,
      };
      this.$store.dispatch("addCashRegisterExpense", { id: this.id, form }).then(() => {
        this.dialogExpense = false;
        this.loadData();
      });
    },
    deleteExpense(item) {
      if (!confirm("¿Eliminar este gasto?")) return;
      this.$store.dispatch("deleteCashRegisterExpense", item.id).then(() => {
        this.loadData();
      });
    },
    doClose() {
      if (!this.$refs.formClose.validate()) return;
      this.$store.dispatch("closeCashRegister", { id: this.id, form: { actual_cash: this.closeForm.actual_cash, notes: this.closeForm.notes } }).then(() => {
        this.dialogClose = false;
        this.loadData();
      });
    },
    cashDiffC() {
      if (!this.register) return 0;
      return (this.register.actual_cash || 0) - (this.register.cash_income || 0) - (this.register.opening_balance || 0) + this.totalExpenses;
    },
    sourceColor(type) {
      return { loan: "primary", sale: "success", retail_sale: "orange", photo_studio: "purple" }[type] || "grey";
    },
    sourceLabel(type) {
      return { loan: "Alquiler", sale: "Venta", retail_sale: "Venta retail", photo_studio: "Foto estudio" }[type] || type;
    },
    paymentMethodLabel(m) {
      const labels = { CASH: "Efectivo", TRANSFER: "Transferencia", DATAPHONE: "Datáfono", GIFT_CARD: "Gift Card" };
      return labels[m] || m;
    },
  },
};
</script>
